/**
 * Cart Actions - Business logic for cart operations
 *
 * Unified handler for adding products to cart from any UI component.
 * Works with both ProductHit and ProductResult types.
 * Handles Aspro notifications and counter updates.
 */

import { ProductHit } from "../../types/searchResults";
import { ProductResult } from "../../search-core/types/ProductResult";
import { ApiCartService } from "../api-cart-service";

/**
 * Add product to cart with unified handling
 *
 * @param product - Product data (ProductHit or ProductResult)
 * @returns Promise resolving to success status
 */
export async function addToCart(product: ProductHit | ProductResult): Promise<boolean> {
  try {
    // ID отправляется как есть (число или код 1С вида "00000128838" / "ЦБ-00000800").
    // parseInt здесь ломал коды: "00000128838" превращался в чужой товар 128838,
    // "ЦБ-..." давал NaN и тихий отказ. Резолв кода делает backend (cart_add.php).
    const productId = product.id;

    if (productId === undefined || productId === null || productId === "") {
      console.error("Invalid product ID:", product.id);
      return false;
    }

    // Add to cart via API service
    const cartService = new ApiCartService();
    const response = await cartService.addToCart(productId, 1);

    if (response?.success) {
      // 🔹 1. Update Aspro counters
      if (typeof window !== "undefined" && (window as any).reloadBasketCounters) {
        (window as any).reloadBasketCounters();
      }

      // 🔹 2. Show Aspro notification
      if (typeof window !== "undefined" && (window as any).JNoticeSurface) {
        const surface = (window as any).JNoticeSurface.get();
        const BX = (window as any).BX;

        // Extract product title based on type
        let productTitle = "";
        if ("title" in product) {
          // ProductResult - title is required string
          const result = product as ProductResult;
          productTitle = result.title;
        } else if ("feed" in product) {
          // ProductHit - name might be string or undefined
          const hit = product as ProductHit;
          productTitle = hit.feed.name || "";
        }

        // Extract product image
        let productImage = "";
        if ("image" in product) {
          // ProductResult - image is optional string
          const result = product as ProductResult;
          productImage = result.image || "";
        } else if ("feed" in product) {
          // ProductHit - picture might be string or undefined
          const hit = product as ProductHit;
          productImage = hit.feed.picture || "";
        }

        surface.create({
          closeable: true,
          autoclose: 3000,
          type: "add2cart",
          link: (window as any).arAsproOptions?.PAGES?.BASKET_PAGE_URL || "/basket/",
          content: {
            title:
              typeof BX !== "undefined" && BX.message
                ? BX.message("NOTICE_ADDED2CART")
                : "Товар добавлен в корзину",
            detail: productTitle,
            image: productImage,
          },
        });
      }

      console.log("Added to cart:", response);
      return true;
    }

    console.warn("Cart addition response indicates failure:", response);
    return false;
  } catch (error) {
    console.error("Failed to add to cart:", error);
    return false;
  }
}

/**
 * Check if product has variants or is out of stock
 * (Utility function for UI components to determine if redirect is needed)
 *
 * @param product - Product data (ProductHit or ProductResult)
 * @returns true if product should redirect to detail page instead of adding to cart
 */
export function shouldRedirectToProductPage(product: ProductHit | ProductResult): boolean {
  // Check for variants
  let variantsCount = 0;
  if ("variantsCount" in product) {
    // ProductResult - variantsCount is number | undefined
    const result = product as ProductResult;
    variantsCount = result.variantsCount !== undefined ? result.variantsCount : 0;
  }

  // Check availability
  let available = true;
  if ("available" in product) {
    // ProductResult - available is boolean | undefined
    const result = product as ProductResult;
    available = result.available !== undefined ? result.available : true;
  } else if ("feed" in product) {
    // ProductHit - available in feed
    const hit = product as ProductHit;
    available = hit.feed.available !== undefined ? hit.feed.available : true;
  }

  const hasVariants = variantsCount > 0;
  const isOutOfStock = available === false;

  return hasVariants || isOutOfStock;
}
